import React, { useCallback, useState } from 'react';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';

import { Button, ButtonIntent, ButtonType } from 'app/design-system/components/Button';
import { Input } from 'app/design-system/components/Input';
import { Modal } from 'app/design-system/components/Modal';
import { useTranslation } from 'app/lang';
import { useSSR } from 'app/packages/components';

import { closeEmailCollectorDialog, sendRequest } from '../actions';
import { getValidate } from './EmailCollectorUtils';

import dictionary from './EmailCollector.dictionary.json';

import styles from './EmailCollector.module.scss';

export type InitialValues = {
  email: string;
};

const PATH_REGISTER_EMAIL = '/api/v1/rest/register-email/';

const EmailCollectorContent = (): JSX.Element => {
  const isSSR = useSSR();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [reCaptchaToken, setReCaptchaToken] = useState('');

  const validate = getValidate(t);

  const handleClose = useCallback(() => {
    dispatch(closeEmailCollectorDialog());
  }, []);

  const handleFormSubmit = useCallback((values: InitialValues) => {
    const { email } = values;
    sendRequest(PATH_REGISTER_EMAIL, { email }, { headers: { 'X-Captcha-Token': reCaptchaToken } })
      .then(() => dispatch(closeEmailCollectorDialog()));
  }, []);

  const handleVerify = useCallback((token) => {
    setReCaptchaToken(token);
  }, [setReCaptchaToken]);

  return (
    <>
      {!isSSR && (
        <GoogleReCaptcha onVerify={handleVerify} />
      )}
      <Modal.Header className={styles.modalHeader}
                    data-test="Modal-EmailCollector-header">
        {t(dictionary.modalHeader)}
      </Modal.Header>
      <Formik
        initialValues={{ email: '' }}
        validate={validate}
        onSubmit={handleFormSubmit}
        data-test="Modal-EmailCollector-form">
        {({
          values,
          errors,
          handleChange,
          handleSubmit,
          isSubmitting,
        }) => (
          <form
            noValidate
            onSubmit={handleSubmit}
            data-test="Modal-EmailCollector-form">
            <Modal.Body className={styles.modalBody}
                        data-test="Modal-EmailCollector-body">
              <p className={styles.bodyInfo}>
                {t(dictionary.modalBody)}
              </p>
              <Input
                id="email"
                name="email"
                value={values.email}
                onChange={handleChange}
                placeholder="Email"
                type="email"
                error={!!errors.email}
                autoFocus
                className={styles.fieldInput}
                autoComplete="off"
                data-test="Modal-EmailCollector-Input" />
            </Modal.Body>
            <Modal.Footer className={styles.modalFooter}
                          data-test="Modal-EmailCollector-footer">
              <Button type={ButtonType.BUTTON}
                      intent={ButtonIntent.DEFAULT}
                      disabled={isSubmitting}
                      onClick={handleClose}>
                {t(dictionary.footerButton)}
              </Button>
              <Button type={ButtonType.SUBMIT}
                      intent={ButtonIntent.PRIMARY}
                      disabled={isSubmitting}
                      loading={isSubmitting}>
                {t(dictionary.footerButtonSubmit)}
              </Button>
            </Modal.Footer>
          </form>
        )}
      </Formik>
    </>
  );
};

export default EmailCollectorContent;
